import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Package,
  CheckCircle2,
  XCircle,
  Plus,
  Minus,
  Pencil,
  Trash2,
  X,
  Loader2,
  ImagePlus,
  Search,
  Filter,
  ImageOff,
  Boxes,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${API_BASE}/api/stock`;

const DEFAULT_CATEGORIES = ["Frames", "Lenses", "Sunglasses", "Accessories", "Contact Lens"];

const emptyForm = {
  product_name: "",
  sku: "",
  category: "Frames",
  customCategory: "",
  brand: "",
  quantity: 0,
  mrp: "",
  price: "",
  branch: "Z.A. Optical Johripur",
  vendor: "",
  description: "",
};

const inr = (n) => (n == null || n === "" ? "—" : `₹ ${Number(n).toLocaleString("en-IN")}`);
const imgSrc = (img) => (img?.filename ? `${API_BASE}/api/uploads/stock/${img.filename}` : "");

function Thumb({ img, size = "h-11 w-11" }) {
  if (!img) {
    return (
      <div className={`${size} rounded-lg bg-slate-100 grid place-items-center text-slate-400 shrink-0`}>
        <ImageOff size={16} />
      </div>
    );
  }
  return (
    <img
      src={imgSrc(img)}
      alt=""
      className={`${size} rounded-lg object-cover shrink-0 border border-slate-200`}
      onError={(e) => (e.target.style.display = "none")}
    />
  );
}

const Stock = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [newFiles, setNewFiles] = useState([]); // File[]
  const [existingImages, setExistingImages] = useState([]); // [{filename,...}]
  const [previewLightbox, setPreviewLightbox] = useState(null);
  const fileInputRef = useRef(null);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError("");
      const [s, c] = await Promise.all([
        axios.get(API_URL),
        axios.get(`${API_URL}/categories`),
      ]);
      setProducts(s.data.products || []);
      setCategories(c.data.categories || DEFAULT_CATEGORIES);
    } catch (err) {
      setError("Failed to load stock.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        p.product_name?.toLowerCase().includes(q) ||
        p.sku?.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.vendor?.toLowerCase().includes(q);
      const matchCat = !category || p.category === category;
      const matchStatus = !statusFilter || p.status === statusFilter;
      return matchSearch && matchCat && matchStatus;
    });
  }, [products, search, category, statusFilter]);

  const totalProducts = products.length;
  const inStock = products.filter((p) => p.status === "In Stock").length;
  const outOfStock = products.filter((p) => p.status === "Out of Stock").length;

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setNewFiles([]);
    setExistingImages([]);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    const isCustom = !DEFAULT_CATEGORIES.includes(item.category);
    setEditingId(item.id);
    setForm({
      product_name: item.product_name || "",
      sku: item.sku || "",
      category: isCustom ? "__custom__" : item.category || "Frames",
      customCategory: isCustom ? item.category : "",
      brand: item.brand || "",
      quantity: item.quantity ?? 0,
      mrp: item.mrp ?? "",
      price: item.price ?? "",
      branch: item.branch || "Z.A. Optical Johripur",
      vendor: item.vendor || "",
      description: item.description || "",
    });
    setNewFiles([]);
    setExistingImages(item.images || []);
    setModalOpen(true);
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const total = existingImages.length + newFiles.length + picked.length;
    if (total > 5) {
      alert(`Only 5 images allowed. You already have ${existingImages.length + newFiles.length}.`);
      e.target.value = "";
      return;
    }
    setNewFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };

  const removeExistingImage = (filename) =>
    setExistingImages((prev) => prev.filter((i) => i.filename !== filename));
  const removeNewFile = (idx) =>
    setNewFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.product_name.trim()) return alert("Product name required.");
    const finalCategory =
      form.category === "__custom__" ? form.customCategory.trim() : form.category;
    if (!finalCategory) return alert("Category required.");
    if (!form.branch) return alert("Branch required.");

    const productPayload = {
      product_name: form.product_name.trim(),
      sku: form.sku,
      category: finalCategory,
      brand: form.brand,
      quantity: Number(form.quantity) || 0,
      mrp: form.mrp === "" ? null : Number(form.mrp),
      price: form.price === "" ? null : Number(form.price),
      branch: form.branch,
      vendor: form.vendor,
      description: form.description,
    };

    const fd = new FormData();
    fd.append("product", JSON.stringify(productPayload));
    newFiles.forEach((f) => fd.append("images", f));
    if (editingId) {
      fd.append("existingImages", JSON.stringify(existingImages.map((i) => i.filename)));
    }

    try {
      setSaving(true);
      if (editingId) {
        await axios.put(`${API_URL}/${editingId}`, fd);
      } else {
        await axios.post(API_URL, fd);
      }
      setModalOpen(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API_URL}/${id}`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete.");
    }
  };

  const adjustStock = async (id, delta) => {
    try {
      const { data } = await axios.post(`${API_URL}/${id}/adjust`, { delta });
      setProducts((prev) => prev.map((p) => (p.id === id ? data.product : p)));
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update quantity.");
    }
  };

  const totalSlotsLeft = 5 - existingImages.length - newFiles.length;

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Stock</h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage products, images, MRP and inventory.
          </p>
        </div>
        <button
          data-testid="add-stock-btn"
          type="button"
          onClick={openAdd}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 font-medium text-white hover:bg-slate-800 transition w-full sm:w-auto"
        >
          <Plus size={17} />
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm flex flex-col md:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3">
          <Search size={16} className="text-slate-400" />
          <input
            data-testid="stock-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search product / SKU / brand / vendor"
            className="w-full bg-transparent py-2.5 text-sm outline-none"
          />
        </div>
        <div className="grid grid-cols-2 md:flex gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3">
            <Filter size={15} className="text-slate-400" />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="bg-transparent py-2.5 text-sm outline-none pr-4"
            >
              <option value="">All Cats</option>
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none"
          >
            <option value="">All Status</option>
            <option>In Stock</option>
            <option>Out of Stock</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white p-3 sm:p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="hidden sm:grid h-11 w-11 place-items-center rounded-xl bg-blue-50">
              <Package className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Total</p>
              <h2 data-testid="stock-total" className="text-xl sm:text-3xl font-bold text-slate-900">
                {totalProducts}
              </h2>
            </div>
          </div>
        </div>
        <div className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white p-3 sm:p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="hidden sm:grid h-11 w-11 place-items-center rounded-xl bg-emerald-50">
              <CheckCircle2 className="text-emerald-600" size={20} />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">In Stock</p>
              <h2 className="text-xl sm:text-3xl font-bold text-emerald-600">{inStock}</h2>
            </div>
          </div>
        </div>
        <div className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white p-3 sm:p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="hidden sm:grid h-11 w-11 place-items-center rounded-xl bg-red-50">
              <XCircle className="text-red-600" size={20} />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Out</p>
              <h2 className="text-xl sm:text-3xl font-bold text-red-600">{outOfStock}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center">
          <Loader2 className="mx-auto animate-spin text-blue-600" size={30} />
          <p className="mt-3 text-sm text-slate-500">Loading stock…</p>
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-slate-100">
            <Boxes className="text-slate-400" size={26} />
          </div>
          <h3 className="mt-3 text-base font-semibold text-slate-700">No products yet</h3>
          <p className="text-sm text-slate-500">Add your first frame or lens above.</p>
        </div>
      ) : (
        <>
          {/* Desktop / Tablet Table */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="stock-table">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-600 text-xs uppercase tracking-wider">
                    <th className="px-4 py-3 font-semibold">Image</th>
                    <th className="px-4 py-3 font-semibold">Product</th>
                    <th className="px-4 py-3 font-semibold">Category</th>
                    <th className="px-4 py-3 font-semibold">MRP</th>
                    <th className="px-4 py-3 font-semibold">Price</th>
                    <th className="px-4 py-3 font-semibold">Qty</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => item.images?.[0] && setPreviewLightbox(item.images[0])}
                          className="block"
                        >
                          <Thumb img={item.images?.[0]} size="h-12 w-12" />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-800">{item.product_name}</p>
                        <p className="text-xs text-slate-500">
                          {item.brand ? `${item.brand} · ` : ""}
                          SKU: {item.sku}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-blue-50 text-blue-700 px-2.5 py-0.5 text-xs font-medium">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 line-through">{inr(item.mrp)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-900">{inr(item.price)}</td>
                      <td className="px-4 py-3">
                        <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white">
                          <button
                            data-testid={`stock-decr-${item.id}`}
                            onClick={() => adjustStock(item.id, -1)}
                            className="h-8 w-8 grid place-items-center text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-l-lg transition"
                            aria-label="Out one"
                            disabled={item.quantity <= 0}
                          >
                            <Minus size={14} />
                          </button>
                          <span
                            data-testid={`stock-qty-${item.id}`}
                            className="min-w-[38px] px-2 text-center text-sm font-semibold text-slate-900 border-x border-slate-200"
                          >
                            {item.quantity}
                          </span>
                          <button
                            data-testid={`stock-incr-${item.id}`}
                            onClick={() => adjustStock(item.id, 1)}
                            className="h-8 w-8 grid place-items-center text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-r-lg transition"
                            aria-label="Add one"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            item.status === "In Stock"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-red-50 text-red-700"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            data-testid={`edit-stock-${item.id}`}
                            onClick={() => openEdit(item)}
                            className="h-9 w-9 grid place-items-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            data-testid={`delete-stock-${item.id}`}
                            onClick={() => handleDelete(item.id)}
                            className="h-9 w-9 grid place-items-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden grid grid-cols-1 gap-3">
            {filtered.map((item) => (
              <div
                key={item.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex gap-3">
                  <button onClick={() => item.images?.[0] && setPreviewLightbox(item.images[0])}>
                    <Thumb img={item.images?.[0]} size="h-16 w-16" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{item.product_name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {item.brand ? `${item.brand} · ` : ""}
                      {item.category}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                      <span className="text-slate-500 line-through">{inr(item.mrp)}</span>
                      <span className="font-semibold text-slate-900">{inr(item.price)}</span>
                      <span
                        className={`ml-auto rounded-full px-2 text-[10px] font-bold ${
                          item.status === "In Stock"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50">
                    <button
                      onClick={() => adjustStock(item.id, -1)}
                      disabled={item.quantity <= 0}
                      className="h-9 w-9 grid place-items-center text-slate-600 hover:text-red-600 disabled:opacity-40"
                    >
                      <Minus size={15} />
                    </button>
                    <span className="min-w-[40px] px-2 text-center text-sm font-bold text-slate-900">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => adjustStock(item.id, 1)}
                      className="h-9 w-9 grid place-items-center text-slate-600 hover:text-emerald-600"
                    >
                      <Plus size={15} />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEdit(item)}
                      className="h-9 px-3 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="h-9 w-9 grid place-items-center rounded-lg bg-red-50 text-red-600"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Lightbox */}
      {previewLightbox && (
        <div
          className="fixed inset-0 z-[60] grid place-items-center bg-black/80 p-4"
          onClick={() => setPreviewLightbox(null)}
        >
          <img
            src={imgSrc(previewLightbox)}
            alt=""
            className="max-h-[85vh] max-w-[90vw] rounded-xl shadow-2xl"
          />
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto"
          onClick={() => !saving && setModalOpen(false)}
        >
          <div
            className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl my-6 max-h-[95vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                {editingId ? "Edit Product" : "Add Product"}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={22} />
              </button>
            </div>
            <form
              onSubmit={handleSubmit}
              className="p-5 overflow-y-auto flex-1 space-y-5"
            >
              {/* Images */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-slate-800">
                    Images ({existingImages.length + newFiles.length}/5)
                  </label>
                  <span className="text-xs text-slate-500">JPG / PNG · up to 5</span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {existingImages.map((img) => (
                    <div key={img.filename} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                      <img src={imgSrc(img)} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(img.filename)}
                        className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                  {newFiles.map((f, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                      <img
                        src={URL.createObjectURL(f)}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                      <span className="absolute top-1 left-1 rounded-full bg-blue-600 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">
                        New
                      </span>
                      <button
                        type="button"
                        onClick={() => removeNewFile(idx)}
                        className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                  {totalSlotsLeft > 0 && (
                    <button
                      type="button"
                      data-testid="stock-image-upload"
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50 text-slate-500 hover:text-blue-600 grid place-items-center transition"
                    >
                      <div className="text-center">
                        <ImagePlus size={22} className="mx-auto" />
                        <span className="mt-1 block text-[10px] uppercase tracking-wider font-semibold">
                          Add
                        </span>
                      </div>
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  data-testid="stock-image-upload-input"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onPickFiles}
                  className="hidden"
                />
              </div>

              {/* Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-700">Product Name *</label>
                  <input
                    data-testid="stock-form-name"
                    type="text"
                    required
                    value={form.product_name}
                    onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                    placeholder="Ray-Ban Aviator Classic"
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">SKU</label>
                  <input
                    type="text"
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                    placeholder="Auto-generated if blank"
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Brand</label>
                  <input
                    type="text"
                    value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    placeholder="Ray-Ban"
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Category *</label>
                  <select
                    data-testid="stock-form-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    {categories.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                    <option value="__custom__">+ Add new category…</option>
                  </select>
                  {form.category === "__custom__" && (
                    <input
                      autoFocus
                      value={form.customCategory}
                      onChange={(e) =>
                        setForm({ ...form, customCategory: e.target.value })
                      }
                      placeholder="New category name"
                      className="mt-2 w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Branch *</label>
                  <select
                    value={form.branch}
                    onChange={(e) => setForm({ ...form, branch: e.target.value })}
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option>Z.A. Optical Johripur</option>
                    <option>Z.A. Optical Jhilmil</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">MRP (₹)</label>
                  <input
                    data-testid="stock-form-mrp"
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.mrp}
                    onChange={(e) => setForm({ ...form, mrp: e.target.value })}
                    placeholder="2499"
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Sell Price (₹)</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="1999"
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Quantity *</label>
                  <input
                    data-testid="stock-form-quantity"
                    type="number"
                    min={0}
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Vendor</label>
                  <input
                    type="text"
                    value={form.vendor}
                    onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                    placeholder="Vendor name"
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-700">Description</label>
                  <textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Frame details, material, size…"
                    className="mt-1 w-full rounded-lg border border-slate-300 p-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2 border-t border-slate-100 mt-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="rounded-lg border border-slate-300 px-5 py-2.5 text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  data-testid="stock-form-submit"
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-5 py-2.5 text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  {saving && <Loader2 size={16} className="animate-spin" />}
                  {editingId ? "Update Product" : "Save Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stock;
